import { useEffect, useState } from "react";
import { getObject, setObject } from "@/utils/storage-helpers";

/**
 * 【临时】自绘定位授权弹窗（模拟系统权限框）的开合与应答通道。
 *
 * 为什么要这么一层：浏览器那个系统权限框长什么样、有几个选项，全由系统决定，
 * 设计稿改不动它；而且它是「一次性」的 —— 用户点过禁止之后就再也不弹了，
 * 联调时很难复现。所以先做一个自绘的顶上，样式可控、可反复触发。
 *
 * 通道设计：requestMockLocationPermission() 返回一个挂起的 promise，
 * 弹窗组件拿到用户的选择后调 answerMockLocationPermission() 把它 resolve 掉。
 * 这样上游（geolocation → store → gate）一行都不用改，
 * 换回真实权限框时只要把 geolocation.ts 里那个分支去掉。
 *
 * 订阅方式照抄 components/chatMount.ts 的写法，保持与仓库既有习惯一致。
 */

/**
 * 用户上次的选择记在这里。
 *
 * 用 utils 那套 storage：logout() 里的 _clearObject() 会把它清掉（它不在 keepDataKeys），
 * 而刷新页面不会。于是「刷新不再问、退出后重新登录才再问」这件事不用额外去检测
 * 「这次到底是登录还是刷新」—— 有记录就不问，没记录就问。
 */
const STORAGE_KEY = "compliance-geo-permission";

const readStoredAnswer = (): boolean | null => {
  const raw = getObject(STORAGE_KEY);
  if (raw === "1") return true;
  if (raw === "0") return false;
  return null;
};

/** 正在等用户回答的那次请求；没有请求在挂起时为 null */
let pendingResolve: ((granted: boolean) => void) | null = null;
let sheetVisible = false;
const listeners = new Set<() => void>();

const emit = (): void => {
  listeners.forEach(fn => {
    try {
      fn();
    } catch {
      /* 单个订阅者异常不影响其他订阅者 */
    }
  });
};

/**
 * 弹出授权弹窗并等用户选择。true = 允许，false = 禁止。
 * 已经问过（刷新后）就直接用上次的选择，不再弹。
 */
export const requestMockLocationPermission = (): Promise<boolean> => {
  const stored = readStoredAnswer();
  if (stored !== null) return Promise.resolve(stored);

  // 上一次还挂着就先按「禁止」了结，避免 promise 永远不落地把调用方卡死。
  // 正常路径不会走到这里：runGeoCheck 自身已经做了在飞去重。
  if (pendingResolve) {
    const stale = pendingResolve;
    pendingResolve = null;
    stale(false);
  }

  return new Promise<boolean>(resolve => {
    pendingResolve = resolve;
    sheetVisible = true;
    emit();
  });
};

/** 由弹窗组件调用：把用户的选择交回去并关闭弹窗。 */
export const answerMockLocationPermission = (granted: boolean): void => {
  setObject(STORAGE_KEY, granted ? "1" : "0");
  const resolve = pendingResolve;
  pendingResolve = null;
  sheetVisible = false;
  emit();
  resolve?.(granted);
};

/** 弹窗组件用：当前是否该显示。 */
export const useMockLocationPermissionVisible = (): boolean => {
  const [visible, setVisible] = useState(sheetVisible);

  useEffect(() => {
    const sync = () => setVisible(sheetVisible);
    listeners.add(sync);
    // 订阅建立前可能已经被置位，补一次检查避免丢事件
    sync();
    return () => {
      listeners.delete(sync);
    };
  }, []);

  return visible;
};

/**
 * 直接把「已授权」记下来，不弹任何东西。
 *
 * 给地理围栏弹窗的 Enable Location 用：那个按钮点下去就当用户开了定位，
 * 不再把授权弹窗重新弹一遍 —— 同一件事问两次，用户只会觉得点了没用。
 */
export const grantLocationPermission = (): void => {
  setObject(STORAGE_KEY, "1");
};
